import React from "react";
import { Box, Fab, FabProps } from "@mui/material";

export interface CenteredFabProps {
  /** The icon to show inside the FAB */
  icon: React.ReactNode;
  /** Accessible label */
  label: string;
  /** Color of the FAB */
  color?: FabProps["color"];
  /** Size of the FAB */
  size?: FabProps["size"];
  /** Called when the FAB is clicked */
  onClick?: () => void;
  /** Position of the FAB inside the container */
  position?: "start" | "center" | "end";
  /** Custom styles for the FAB */
  sx?: any;
}

/**
 * A floating action button centered horizontally with configurable position.
 */
export const CenteredFab: React.FC<CenteredFabProps> = ({
  icon,
  label,
  color = "primary",
  size = "medium",
  onClick,
  position = "end",
  sx,
}) => {
  // Map position prop to justifyContent
  const justifyContentMap: Record<
    NonNullable<CenteredFabProps["position"]>,
    string
  > = {
    start: "flex-start",
    center: "center",
    end: "flex-end",
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 84,
        // Responsive positioning to match GridWrapper layout
        // xs: full width (12 columns) - no offset
        left: { xs: -40, sm: "10%", md: "30%" },
        // Responsive width to match GridWrapper content area
        // xs: 12/12 = 100%, sm: 8/12 = 66.67%, md: 4/12 = 33.33%
        width: { xs: "100%", sm: "66.67%", md: "33.33%" },
        // Center the content within the container
        display: "flex",
        justifyContent: justifyContentMap[position],
        px: 2,
        zIndex: (theme) => 400,
        pointerEvents: "none", // Allow clicks to pass through the container
      }}
    >
      <Fab
        size={size}
        color={color}
        aria-label={label}
        onClick={onClick}
        sx={{ 
          pointerEvents: "auto", // Ensure FAB can receive clicks
          ...sx // Merge custom styles
        }}
      >
        {icon}
      </Fab>
    </Box>
  );
};

export default CenteredFab;
